import { Component, OnInit } from '@angular/core';
import {
  SessionService,
  AuthenticationEvent,
} from 'src/app/sharedFeatures/services/session.service';
import { CurrentUserService } from 'src/app/sharedFeatures/services/current-user.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

/**
 * Component for security settings and authentication history
 * SECURITY FIX: VAPT Issue #3 - Iteration 2
 *
 * Provides:
 * - Authentication history (login/logout events)
 * - Logout from all devices functionality
 * - Security recommendations
 */
@Component({
  selector: 'dct-security-settings',
  templateUrl: './security-settings.component.html',
  styleUrls: ['./security-settings.component.scss'],
})
export class SecuritySettingsComponent implements OnInit {
  authHistory: AuthenticationEvent[] = [];
  loading = false;
  loggingOutAll = false;
  historyDays = 30;

  constructor(
    private sessionService: SessionService,
    private currentUserService: CurrentUserService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadAuthHistory();
  }

  /**
   * Load authentication history
   */
  loadAuthHistory(): void {
    this.loading = true;
    this.sessionService.getAuthHistory(this.historyDays).subscribe({
      next: history => {
        this.authHistory = history;
        this.loading = false;
      },
      error: error => {
        console.error('Error loading auth history:', error);
        this.toastr.error(
          this.translate.instant('users.securitySettings.loadFailed'),
          this.translate.instant('shared.error')
        );
        this.loading = false;
      },
    });
  }

  /**
   * Change the history time range and reload
   */
  changeHistoryRange(days: number): void {
    this.historyDays = days;
    this.loadAuthHistory();
  }

  /**
   * Logout from all devices
   */
  logoutFromAllDevices(): void {
    if (
      !confirm(
        this.translate.instant('users.securitySettings.confirmLogoutAll')
      )
    ) {
      return;
    }

    this.loggingOutAll = true;
    this.currentUserService.logOut(false, true);
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleString();
  }

  /**
   * Get event icon based on event type
   */
  getEventIcon(eventType: string): string {
    switch (eventType.toLowerCase()) {
      case 'login':
        return 'box-arrow-in-right';
      case 'logout':
        return 'box-arrow-right';
      case 'refreshtoken':
        return 'arrow-repeat';
      case 'failedlogin':
        return 'exclamation-triangle';
      case 'tokenrevoked':
        return 'x-circle';
      default:
        return 'info-circle';
    }
  }

  /**
   * Get event color based on success and type
   */
  getEventColor(event: AuthenticationEvent): string {
    if (!event.success) {
      return 'danger';
    }

    switch (event.eventType.toLowerCase()) {
      case 'login':
        return 'success';
      case 'logout':
        return 'secondary';
      case 'refreshtoken':
        return 'info';
      case 'tokenrevoked':
        return 'warning';
      default:
        return 'primary';
    }
  }

  /**
   * Get event title
   */
  getEventTitle(event: AuthenticationEvent): string {
    const type = event.eventType;
    const success = event.success;
    const typeKey = `users.securitySettings.eventType.${type.toLowerCase()}`;

    if (!success) {
      return this.translate.instant('users.securitySettings.failedEventTitle', {
        event: this.translate.instant(typeKey),
      });
    }

    return this.translate.instant(typeKey);
  }
}
